import React from 'react';
import { Box, Paper, Grid, Typography, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';

// export default function FinancialMetricsTable(completionList: any, selectedCurrency: any) {
//   const isNegative = (val: any) => val.trim().startsWith('-');

export default function FinancialMetricsTable({ completionList, symbol }: { completionList: any; symbol: string }) {
  const [t] = useTranslation();
  const deltaColor = (val: any) => {
    if (typeof val !== 'string') return '#1a237e';
    const trimmed = val.trim();
    if (trimmed.startsWith('-')) return '#f44336';
    // if (trimmed.startsWith('0')) return '#1a237e';
    return '#1a237e';
  };

  // const tableData = React.useMemo(() => transformIncomeData(completionList), [completionList]);

  const tableData = React.useMemo(() => transformIncomeData({ completionList }, symbol, t), [completionList, t]);

  if (!tableData) {
    return <p>{t('No_Data_Found')}</p>;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        maxWidth: '100%',
        height: '100%', // Force it to match your parent card's height restriction
        overflowY: 'auto', // Enable vertical scrollbar when content is too tall
        overflowX: 'hidden', // Block unwanted side-to-side scrolling shifts
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#1a237e',
        p: 0.5,
        boxSizing: 'border-box',
        color: '#fff',
        fontSize: 14,

        // OPTIONAL: Add a clean modern scrollbar look matching the blue theme
        '&::-webkit-scrollbar': {
          width: '5px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#1a237e',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#e0e0e0',
          borderRadius: '4px',
        },
      }}
    >
      {/* Top Header Section */}
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={4.5}>
          <Tooltip
            placement="bottom-end"
            title={t('Tooltip_PO_Amount')}
            componentsProps={{
              tooltip: {
                sx: {
                  fontSize: 15,
                  padding: '4px 6px',
                  borderRadius: '6px',
                },
              },
            }}
          >
            <Box sx={{ backgroundColor: '#ffffff', color: '#1a237e', p: 0.6, fontWeight: 'bold', fontSize: 15 }}>
               {t('Purchased_Contractual')}
            </Box>
          </Tooltip>
        </Grid>
        <Grid item xs={7.5}>
          <Box
            sx={{
              backgroundColor: '#e0e0e0',
              color: '#1a237e',
              p: 0.6,
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: 14,
            }}
          >
            {tableData.purchasedTotal}
          </Box>
        </Grid>
      </Grid>

      {/* Column Group Headings */}
      <Grid container spacing={0.5} sx={{ m: 0.1, textAlign: 'center' }}>
        <Grid item xs={4.5} />
        <Grid item xs={3.75}>
          <Typography variant="caption" sx={{ color: '#fff', fontSize: 15 }}>
            {t('To_Date')}
          </Typography>
        </Grid>
        <Grid item xs={3.75}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#fff', fontSize: 15 }}>
            {t('At_Completion')}
          </Typography>
        </Grid>
      </Grid>

      {/* Core Operational Financial Rows */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {tableData.rows.map((row) => (
          <Grid container spacing={1} alignItems="center" key={row.id}>
            <Grid item xs={4.5}>
              <Box
                sx={{
                  backgroundColor: row.isHeader ? '#c5cae9' : '#ffffff',
                  color: '#1a237e',
                  p: 0.6,
                  fontSize: 14,
                  fontWeight: row.isHeader ? 'bold' : 'normal',
                  pl: row.indent ? 2.5 : 1,
                }}
              >
                {row.tooltip ? (
                  <Tooltip title={row.tooltip} arrow placement="bottom">
                    <span>{row.label}</span>
                  </Tooltip>
                ) : (
                  row.label
                )}
              </Box>
            </Grid>
            <Grid item xs={3.75}>
              <Box
                sx={{
                  backgroundColor: row.isHeader ? '#c5cae9' : '#e0e0e0',
                  color: '#1a237e',
                  p: 0.6,
                  textAlign: 'center',
                  fontSize: 14,
                  fontWeight: 'bold',
                }}
              >
                {row.toDate}
              </Box>
            </Grid>
            <Grid item xs={3.75}>
              <Box
                sx={{
                  backgroundColor: row.isHeader ? '#c5cae9' : '#e0e0e0',
                  color: '#1a237e',
                  p: 0.6,
                  textAlign: 'center',
                  fontSize: 14,
                  fontWeight: 'bold',
                }}
              >
                {row.atCompletion}
              </Box>
            </Grid>
          </Grid>
        ))}
      </Box>

      {/* Divider Separator Line */}
      <Box sx={{ height: '1px', my: 1 }} />

      {/* Delta Variations Section */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {tableData.deltas.map((delta) => (
          <Grid container spacing={1} alignItems="center" key={delta.id}>
            <Grid item xs={4.5}>
              <Box sx={{ backgroundColor: '#ffffff', color: '#1a237e', p: 0.6, fontSize: 12 }}>{delta.label}</Box>
            </Grid>
            <Grid item xs={3.75}>
              <Box
                sx={{
                  backgroundColor: '#e0e0e0',
                  color: deltaColor(delta.toDate),
                  p: 0.6,
                  textAlign: 'center',
                  fontSize: 14,
                  fontWeight: 'bold',
                }}
              >
                {delta.toDate}
              </Box>
            </Grid>
            <Grid item xs={3.75}>
              <Box
                sx={{
                  backgroundColor: '#e0e0e0',
                  color: deltaColor(delta.atCompletion),
                  p: 0.6,
                  textAlign: 'center',
                  fontSize: 14,
                  fontWeight: 'bold',
                }}
              >
                {delta.atCompletion}
              </Box>
            </Grid>
          </Grid>
        ))}
      </Box>
    </Paper>
  );
}

export const formatCurrency = (
  value: number | string | null | undefined,
  symbol: string | null | undefined
): string => {
  if (value === undefined || value === null) {
    return `0 ${symbol}`;
  }

  // 3. Formats with space separators and appends the dynamic symbol
  const formattedValue = value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  return `${formattedValue} ${symbol}`;
};

export const transformIncomeData = (rawJson: any, symbol: any, t: (key: string) => string) => {
  const data = rawJson.completionList;
  if (!data) return null;

  return {
    purchasedTotal: formatCurrency(data.purchased, symbol),
    rows: [
      {
        id: 'p_assigned',
        label: t('Purchased_Planned'),
        toDate: formatCurrency(data.to_date?.purchased_assigned, symbol),
        atCompletion: formatCurrency(data.at_completion?.purchased_assigned, symbol),
        isHeader: true,
        tooltip: t('Tooltip_Purchased_Planned'),
      },
      {
        id: 'baseline',
        label: t('Baseline'),
        toDate: formatCurrency(data.to_date?.baseline, symbol),
        atCompletion: formatCurrency(data.at_completion?.baseline, symbol),
        indent: true,
        tooltip: t('Tooltip_Baseline'),
      },
      {
        id: 'forecasted',
        label: t('Forecasted'),
        toDate: formatCurrency(data.to_date?.forecast, symbol),
        atCompletion: formatCurrency(data.at_completion?.forecast, symbol),
        indent: true,
        tooltip: t('Tooltip_Forecasted'),
      },
      {
        id: 'accepted',
        label: t('Accepted_Client'),
        toDate: formatCurrency(data.to_date?.accepted_client, symbol),
        atCompletion: formatCurrency(data.at_completion?.accepted_client, symbol),
        indent: true,
        tooltip: t('Tooltip_Accepted_Client'),
      },
      {
        id: 'invoiced',
        label: t('Invoiced'),
        toDate: formatCurrency(data.to_date?.invoiced, symbol),
        atCompletion: formatCurrency(data.at_completion?.invoiced, symbol),
        isHeader: true,
        tooltip: t('Tooltip_Invoiced'),
      },
    ],
    deltas: [
      {
        id: 'd_base',
        label: t('Delta_Forecasted_Baseline'),
        toDate: formatCurrency(data.to_date?.delta_forecast_baseline, symbol),
        atCompletion: formatCurrency(data.at_completion?.delta_forecast_baseline, symbol),
      },
      {
        id: 'd_purch',
        label: t('Delta_Forecasted_Purchased'),
        toDate: formatCurrency(data.to_date?.delta_forecast_purchased, symbol),
        atCompletion: formatCurrency(data.at_completion?.delta_forecast_purchased, symbol),
      },
      {
        id: 'd_inv',
        label: t('Delta_Invoiced_Purchased'),
        toDate: formatCurrency(data.to_date?.delta_invoiced_purchased, symbol),
        atCompletion: formatCurrency(data.at_completion?.delta_invoiced_purchased, symbol),
      },
    ],
  };
};
